// src/utils/store.js — Supabase backend using security definer RPCs
import supabase from './supabaseClient';

// ── Helpers ────────────────────────────────────────────────────────
function validUuid(id) {
  const re = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return id && re.test(id) ? id : crypto.randomUUID();
}

async function getUserId() {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64, mimeType) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

// ── Load all user data in one RPC call ─────────────────────────────
export const bootDb = {
  async loadAll() {
    const { data, error } = await supabase.rpc('get_my_data');
    if (error) { console.error('get_my_data error:', error); return null; }
    return data;
  },
};

// ── Profile ─────────────────────────────────────────────────────────
export const profileDb = {
  async save(profile) {
    const userId = await getUserId();
    if (!userId) return false;
    const { error } = await supabase.from('user_profiles').update({
      contact_name: profile.contactName || null,
      contact_title: profile.contactTitle || null,
      contact_phone: profile.contactPhone || null,
      contact_email: profile.contactEmail || null,
      agency_name: profile.agencyName || null,
      agency_division: profile.agencyDivision || null,
      agency_billing_code: profile.agencyBillingCode || null,
      accounting_officer_name: profile.accountingOfficerName || null,
      accounting_officer_title: profile.accountingOfficerTitle || null,
      accounting_officer_phone: profile.accountingOfficerPhone || null,
      accounting_officer_email: profile.accountingOfficerEmail || null,
      signatory_name: profile.signatoryName || null,
      signatory_title: profile.signatoryTitle || null,
      signatory_phone: profile.signatoryPhone || null,
      signatory_email: profile.signatoryEmail || null,
      signatory_address: profile.signatoryAddress || null,
      signatory_city: profile.signatoryCity || null,
      signatory_zip: profile.signatoryZip || null,
      sco_agency_code: profile.scoAgencyCode || '3355',
      supervisor_name: profile.supervisorName || null,
      supervisor_title: profile.supervisorTitle || null,
      supervisor_phone: profile.supervisorPhone || null,
      supervisor_email: profile.supervisorEmail || null,
      union_hr_name: profile.unionHrName || null,
      union_hr_email: profile.unionHrEmail || null,
      union_lr_name: profile.unionLrName || null,
      union_lr_email: profile.unionLrEmail || null,
      union_cc_emails: profile.unionCcEmails || null,
      updated_at: new Date().toISOString(),
    }).eq('id', userId);
    if (error) console.error('profileDb.save error:', error);
    return !error;
  },
};

// ── PAAL ────────────────────────────────────────────────────────────
export const paalDb = {
  async save(paal) {
    const userId = await getUserId();
    if (!userId) return false;
    // Get agency_id from session storage or fetch it
    const { data: prof } = await supabase
      .from('user_profiles')
      .select('agency_id')
      .eq('id', userId)
      .single();
    if (!prof?.agency_id) { console.error('No agency_id for user'); return false; }
    const { error } = await supabase.from('agency_paal').upsert({
      agency_id: prof.agency_id,
      non_it: paal.nonIT,
      it: paal.it,
      purchasing_auth_num: paal.purchasingAuthNum || null,
      tier: paal.tier,
      effective_date: paal.effectiveDate || null,
      expiration_date: paal.expirationDate || null,
      notes: paal.notes,
      uploaded: paal.uploaded || false,
      file_name: paal.file?.name || null,
      file_size: paal.file?.size || null,
      file_date: paal.file?.date || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'agency_id' });
    if (error) console.error('paalDb.save error:', error);
    return !error;
  },
};

// ── Contracts ──────────────────────────────────────────────────────
export const contractsDb = {
  async save(contract) {
    const userId = await getUserId();
    if (!userId) return false;
    const { data: prof } = await supabase
      .from('user_profiles').select('agency_id').eq('id', userId).single();
    const { error } = await supabase.from('contracts').upsert({
      id: validUuid(contract.id),
      agency_id: prof?.agency_id,
      owner_id: userId,
      title: contract.title,
      contract_num: contract.contractNum || null,
      description: contract.description,
      budget: parseFloat(contract.budget) || null,
      is_it: contract.isIT,
      proc_method: contract.procMethod,
      category: contract.category,
      notes: contract.notes,
      stage: contract.stage,
      stage_history: contract.stageHistory,
      s215: contract.s215,
      exhibits: contract.exhibits,
      exhibit_numbering: contract.exhibitNumbering,
      attachments: contract.attachments,
      amendments: contract.amendments,
      reminders: contract.reminders,
      documents: contract.documents,
      reference_docs: contract.referenceDocs,
      assigned_cm_id: contract.assignedCmId,
      analyst_name: contract.analystName || '',
      analyst_email: contract.analystEmail || '',
      exemption: contract.exemption,
      ota_exemption: contract.otaExemption,
      sb_dvbe_cert_num: contract.sbDvbeCertNum || null,
      contractor_vendor_id: contract.contractorVendorId || null,
      dvbe: contract.dvbe,
      penny_drafts: contract.pennyDrafts,
      general_attachments: contract.general_attachments || [],
      std213: contract.std213 || {},
      handoff: contract.handoff,
      bid_eval: contract.bidEval,
      cal_eprocure: contract.calEProcure,
      archived: contract.archived || false,
      review_status: contract.reviewStatus || null,
      reviewed_by: contract.reviewedBy || null,
      reviewed_at: contract.reviewedAt || null,
      archived_at: contract.archivedAt || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'id' });
    if (error) console.error('contractsDb.save error:', error);
    return !error;
  },

  async get(id) {
    const { data, error } = await supabase
      .from('contracts')
      .select('*')
      .eq('id', id)
      .single();
    if (error || !data) return null;
    return {
      id: data.id, title: data.title, description: data.description,
      ownerId: data.owner_id,
      contractNum: data.contract_num || '',
      budget: data.budget, isIT: data.is_it, procMethod: data.proc_method,
      category: data.category, notes: data.notes, stage: data.stage,
      stageHistory: data.stage_history || [], s215: data.s215 || {},
      exhibits: data.exhibits || [], exhibitNumbering: data.exhibit_numbering || 'alpha',
      attachments: data.attachments || [], amendments: data.amendments || [],
      reminders: data.reminders || [], documents: data.documents || {},
      referenceDocs: data.reference_docs || [], assignedCmId: data.assigned_cm_id,
      analystName: data.analyst_name || '', analystEmail: data.analyst_email || '',
      exemption: data.exemption || {},
      otaExemption: data.ota_exemption || {},
      dvbe: data.dvbe || {},
      sbDvbeCertNum: data.sb_dvbe_cert_num || '',
      contractorVendorId: data.contractor_vendor_id || null,
      pennyDrafts: data.penny_drafts || {},
      general_attachments: data.general_attachments || [],
      std213: data.std213 || {},
      handoff: data.handoff || {},
      bidEval: data.bid_eval || {},
      calEProcure: data.cal_eprocure || {},
      archived: data.archived || false,
      reviewStatus: data.review_status || null,
      reviewedBy: data.reviewed_by || null,
      reviewedAt: data.reviewed_at || null,
      archivedAt: data.archived_at?.split('T')[0] || null,
      createdAt: data.created_at?.split('T')[0], updatedAt: data.updated_at?.split('T')[0],
    };
  },

  async delete(id) {
    const { error } = await supabase.from('contracts').delete().eq('id', id);
    return !error;
  },

  async getMembers(contractId) {
    const { data, error } = await supabase
      .from('contract_members')
      .select('user_id, added_at, user_profiles(contact_name, contact_email, role)')
      .eq('contract_id', contractId);
    if (error) return [];
    return data || [];
  },

  async addMember(contractId, userId) {
    const addedBy = await getUserId();
    const { error } = await supabase.from('contract_members')
      .insert({ contract_id: contractId, user_id: userId, added_by: addedBy });
    return !error;
  },

  async removeMember(contractId, userId) {
    const { error } = await supabase.from('contract_members').delete()
      .eq('contract_id', contractId).eq('user_id', userId);
    return !error;
  },
};

// ── CM Roster ───────────────────────────────────────────────────────
export const cmsDb = {
  async upsertOne(cm, agencyName) {
    const userId = await getUserId();
    if (!userId) return false;
    const { error } = await supabase.from('user_cms').upsert({
      id: validUuid(cm.id),
      user_id: userId,
      name: cm.name,
      title: cm.title || null,
      phone: cm.phone || null,
      email: cm.email || null,
      division: cm.division || null,
      address: cm.address || null,
      active: cm.active !== false,
      cm_profile_id: cm.cmProfileId || null,
    }, { onConflict: 'id' });
    if (error) { console.error('cmsDb.upsertOne error:', error); return false; }
    return true;
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return false;
    const { error } = await supabase.from('user_cms').delete()
      .eq('id', id).eq('user_id', userId);
    return !error;
  },
};

// ── CM Profiles (shared, claimable) ─────────────────────────────────
export const cmProfilesDb = {
  // Upsert a cm_profile row — called by analyst CMRosterView on save
  async upsertFromRoster(cm, agencyName) {
    const userId = await getUserId();
    if (!userId) return null;
    // Try to find existing profile by email or name+agency
    let profileId = cm.cmProfileId || null;
    if (!profileId && cm.email) {
      const { data: existing } = await supabase
        .from('cm_profiles')
        .select('id')
        .ilike('email', cm.email.trim())
        .ilike('agency_name', agencyName || '')
        .maybeSingle();
      if (existing) profileId = existing.id;
    }
    const profileData = {
      agency_name: agencyName || null,
      name: cm.name,
      title: cm.title || null,
      phone: cm.phone || null,
      email: cm.email || null,
      division: cm.division || null,
      addresses: cm.address ? [{ label: 'Main Office', street: cm.address, city: '', state: 'CA', zip: '' }] : [],
      active: cm.active !== false,
      updated_at: new Date().toISOString(),
    };
    if (profileId) {
      // Update existing — but never overwrite claimed_user_id
      const { error } = await supabase.from('cm_profiles')
        .update(profileData).eq('id', profileId);
      if (error) console.error('cmProfilesDb.update error:', error);
      return profileId;
    } else {
      // Create new unclaimed profile
      const { data, error } = await supabase.from('cm_profiles')
        .insert({ ...profileData, created_by: userId })
        .select('id').single();
      if (error) { console.error('cmProfilesDb.insert error:', error); return null; }
      return data?.id || null;
    }
  },

  // Sync cm_profiles changes back to all user_cms rows for this CM
  async syncToRoster(cmProfileId, updates) {
    if (!cmProfileId) return;
    // Find all user_cms rows linked to this cm_profile_id
    const { data: rosterRows } = await supabase
      .from('user_cms')
      .select('id, user_id')
      .eq('cm_profile_id', cmProfileId);
    if (!rosterRows?.length) return;
    // Update each one
    for (const row of rosterRows) {
      await supabase.from('user_cms').update({
        name:     updates.name     || undefined,
        title:    updates.title    || undefined,
        phone:    updates.phone    || undefined,
        email:    updates.email    || undefined,
        division: updates.division || undefined,
        address:  updates.addresses?.[0]?.street || undefined,
      }).eq('id', row.id);
    }
  },
};

// ── Templates ───────────────────────────────────────────────────────
export const templatesDb = {
  async getPdf(id) {
    const userId = await getUserId();
    if (!userId) return null;
    // For private templates, the file lives at userId/id.
    // For agency templates uploaded by a different user, we still try the owner's path
    // because the storage bucket key is keyed by uploader.
    // First attempt: own path. Fall back: query template row for owner's user_id.
    const tryDownload = async (path) => {
      const { data, error } = await supabase.storage.from('template-files').download(path);
      if (error) return null;
      const full = await blobToBase64(data);
      return full.split(',')[1];
    };
    let b64 = await tryDownload(`${userId}/${id}`);
    if (b64) return b64;
    const { data: tpl } = await supabase.from('user_templates')
      .select('user_id').eq('id', id).maybeSingle();
    if (tpl?.user_id && tpl.user_id !== userId) {
      b64 = await tryDownload(`${tpl.user_id}/${id}`);
    }
    return b64;
  },

  async save(template, pdfBase64) {
    const userId = await getUserId();
    if (!userId) return false;
    // Resolve the user's agency_id once so newly-saved rows have agency_id set
    // (needed for agency-scope queries even when scope='private').
    const { data: prof } = await supabase
      .from('user_profiles').select('agency_id').eq('id', userId).single();
    const agencyId = prof?.agency_id || null;

    // CRITICAL: storage key must match the DB row id. If template.id isn't a
    // valid UUID, validUuid() generates a fresh one; we must use that same
    // generated id for the storage upload. Bug fix from pre-existing divergence.
    const dbId = validUuid(template.id);

    const row = {
      id: dbId, user_id: userId,
      name: template.name, type: template.type,
      exhibits: template.exhibits, notes: template.notes,
      file_name: template.fileName, file_size: template.fileSize,
      scope: template.scope || 'private',
      agency_id: agencyId,
      updated_at: new Date().toISOString(),
    };
    if (template.extracted_profile !== undefined) row.extracted_profile = template.extracted_profile;
    if (template.is_agency_default !== undefined) row.is_agency_default = !!template.is_agency_default;
    if (template.extracted_at !== undefined) row.extracted_at = template.extracted_at;
    // Canonical / personal_reference distinction. Defaults to personal_reference
    // (analyst-uploaded sample). Agency admins can promote a row to canonical
    // via the library UI — exactly one canonical per (agency, contract type) is
    // enforced by a unique partial index on the DB. Step 2 wiring.
    if (template.reference_kind !== undefined) row.reference_kind = template.reference_kind;
    // exhibit_page_ranges: JSON of { "A": { start, end, type: "pages" | "paragraphs" } }.
    // Populated by the Template Slicer Wizard when a multi-exhibit doc is sliced;
    // each slice carries the page or paragraph range of its single exhibit.
    if (template.exhibit_page_ranges !== undefined) row.exhibit_page_ranges = template.exhibit_page_ranges;
    if (template.exhibit_detection_confidence !== undefined) row.exhibit_detection_confidence = template.exhibit_detection_confidence;

    const { error } = await supabase.from('user_templates').upsert(row, { onConflict: 'id' });
    if (error) { console.error('save_template error:', error); return false; }
    if (pdfBase64) {
      const blob = base64ToBlob(pdfBase64, 'application/pdf');
      await supabase.storage.from('template-files')
        .upload(`${userId}/${dbId}`, blob, { upsert: true });
    }
    return true;
  },

  // Promote a template to canonical. Per Step 2.5, canonical is
  // per-(agency, contract type, EXHIBIT). Auto-swap behavior:
  // if another template is already canonical for the same triple, it
  // gets demoted to personal_reference first.
  //
  // RULE: Only single-exhibit rows can be canonical. Multi-exhibit rows
  // (e.g., an un-sliced combined template with exhibits ["A","B","D"])
  // are rejected with reason='multi_exhibit'. UI gates this before the
  // call, but we re-check here as defense-in-depth.
  //
  // Two-step write (no transaction available from the JS SDK):
  //   1. Demote any existing canonical for the same (agency, type, exhibit)
  //   2. Promote the target row to canonical
  //
  // Caller responsibilities:
  //   - Only agency_super_admin should be able to invoke this (UI gate)
  //   - On rare failure between step 1 and step 2, neither row will be
  //     canonical for this triple. User can retry.
  async promoteToCanonical(templateId) {
    const userId = await getUserId();
    if (!userId || !templateId) return { ok: false, reason: 'unauthenticated' };

    // Read the target row to find its agency, type, and exhibit
    const { data: target, error: getErr } = await supabase
      .from('user_templates')
      .select('id, agency_id, type, exhibits, reference_kind')
      .eq('id', templateId).maybeSingle();
    if (getErr || !target) {
      console.error('promoteToCanonical: target not found', getErr);
      return { ok: false, reason: 'not_found' };
    }
    if (target.reference_kind === 'canonical') {
      return { ok: true, swapped: false, alreadyCanonical: true };
    }
    if (!target.agency_id) {
      return { ok: false, reason: 'no_agency' };
    }
    // Enforce single-exhibit rule
    if (!Array.isArray(target.exhibits) || target.exhibits.length !== 1) {
      return { ok: false, reason: 'multi_exhibit', exhibitCount: target.exhibits?.length || 0 };
    }
    const exhibit = target.exhibits[0];

    // Find existing canonical for this (agency, type, exhibit), excluding target.
    // We can't filter on `exhibits[1] = exhibit` directly via JS client array
    // filtering, so we fetch all canonicals for (agency, type) and filter in JS.
    // This is cheap because there are at most ~5-7 candidate exhibits.
    const { data: existing, error: findErr } = await supabase
      .from('user_templates')
      .select('id, name, exhibits')
      .eq('agency_id', target.agency_id)
      .eq('type', target.type)
      .eq('reference_kind', 'canonical')
      .neq('id', templateId);
    if (findErr) {
      console.error('promoteToCanonical: find existing failed', findErr);
      return { ok: false, reason: 'lookup_failed' };
    }

    // Filter to only those rows that have THIS exhibit (the new rule guarantees
    // single-exhibit, but be defensive in case legacy multi-exhibit rows slipped
    // through).
    const conflicts = (existing || []).filter(e =>
      Array.isArray(e.exhibits) && e.exhibits.length === 1 && e.exhibits[0] === exhibit
    );

    let demoted = null;
    if (conflicts.length > 0) {
      demoted = conflicts[0];
      const { error: demoteErr } = await supabase
        .from('user_templates')
        .update({ reference_kind: 'personal_reference', updated_at: new Date().toISOString() })
        .in('id', conflicts.map(c => c.id));
      if (demoteErr) {
        console.error('promoteToCanonical: demote failed', demoteErr);
        return { ok: false, reason: 'demote_failed' };
      }
    }

    // Now promote the target
    const { error: promoteErr } = await supabase
      .from('user_templates')
      .update({ reference_kind: 'canonical', updated_at: new Date().toISOString() })
      .eq('id', templateId);
    if (promoteErr) {
      console.error('promoteToCanonical: promote failed', promoteErr);
      return { ok: false, reason: 'promote_failed', demoted };
    }
    return { ok: true, swapped: !!demoted, demoted, exhibit };
  },

  // Demote a canonical template to personal_reference. The unique index lets
  // any number of personal_reference rows coexist, so no cascading effect.
  async demoteToPersonalReference(templateId) {
    const userId = await getUserId();
    if (!userId || !templateId) return false;
    const { error } = await supabase.from('user_templates')
      .update({ reference_kind: 'personal_reference', updated_at: new Date().toISOString() })
      .eq('id', templateId);
    if (error) { console.error('demoteToPersonalReference error:', error); return false; }
    return true;
  },


  // Persist just the extracted profile JSON for an existing template
  async saveExtractedProfile(id, profileJson) {
    const userId = await getUserId();
    if (!userId) return false;
    const { error } = await supabase.from('user_templates').update({
      extracted_profile: profileJson,
      extracted_at: new Date().toISOString(),
    }).eq('id', id).eq('user_id', userId);
    if (error) { console.error('saveExtractedProfile error:', error); return false; }
    return true;
  },

  // Promote/demote between private and agency scope
  async setScope(id, scope) {
    const userId = await getUserId();
    if (!userId) return false;
    const { error } = await supabase.from('user_templates').update({ scope })
      .eq('id', id).eq('user_id', userId);
    return !error;
  },

  async delete(id) {
    const userId = await getUserId();
    if (!userId) return false;
    await supabase.storage.from('template-files').remove([`${userId}/${id}`]);
    const { error } = await supabase.from('user_templates').delete()
      .eq('id', id).eq('user_id', userId);
    return !error;
  },

  // List templates visible to current user — RLS filters by scope automatically.
  // Includes both own private templates and agency-wide templates.
  async listVisible() {
    const { data, error } = await supabase.from('user_templates').select('*')
      .order('updated_at', { ascending: false });
    if (error) { console.error('listVisible templates:', error); return []; }
    return (data || []).map(t => ({
      id: t.id, name: t.name, type: t.type,
      exhibits: t.exhibits || [], notes: t.notes,
      fileName: t.file_name, fileSize: t.file_size,
      scope: t.scope || 'private',
      agencyId: t.agency_id || null,
      ownerId: t.user_id,
      isAgencyDefault: !!t.is_agency_default,
      extractedProfile: t.extracted_profile || null,
      extractedAt: t.extracted_at || null,
      // Canonical / personal_reference. Default to personal_reference when
      // null (legacy rows pre-dating the column).
      reference_kind: t.reference_kind || 'personal_reference',
      exhibit_page_ranges: t.exhibit_page_ranges || null,
      exhibit_detection_confidence: t.exhibit_detection_confidence || null,
      createdAt: t.created_at?.split('T')[0],
      updatedAt: t.updated_at?.split('T')[0],
    }));
  },
};

// ── Agency-level data: doc template profile ─────────────────────────
// Reads/writes the `doc_template` JSON column on the `agencies` table.
// Used as the agency-wide fallback when no specific Contract Template Library
// match is found at draft time.
export const agencyDb = {
  // Returns the agency's doc_template JSON (or null if not set / not in an agency)
  async getDocTemplate(agencyId = null) {
    const id = agencyId || await currentAgencyId();
    if (!id) return null;
    const { data, error } = await supabase.from('agencies')
      .select('doc_template').eq('id', id).maybeSingle();
    if (error) { console.error('getDocTemplate error:', error); return null; }
    return data?.doc_template || null;
  },

  // Saves the doc_template JSON for an agency. RLS gates this on the server side
  // (super admin or matching agency admin role).
  async saveDocTemplate(agencyId, profileJson) {
    if (!agencyId) return false;
    const { error } = await supabase.from('agencies')
      .update({ doc_template: profileJson }).eq('id', agencyId);
    if (error) { console.error('saveDocTemplate error:', error); return false; }
    return true;
  },

  // ── PAAL document operations ─────────────────────────────────────────
  // PAAL (Purchasing Authority Approval) is the DGS-issued document defining
  // agency dollar thresholds for self-approving contracts vs requiring DGS
  // routing. The PDF is stored in the `paal-files` bucket at {agency_id}/paal.pdf
  // and structured thresholds extracted by Penny live in doc_template.paal.

  // Upload (or replace) the agency's PAAL document. Returns true on success.
  // base64 is the PDF as raw base64 (no data URI prefix). fileName is the
  // original filename, stored on agencies.paal_file_name for display.
  async uploadPaalFile(agencyId, base64, fileName) {
    if (!agencyId || !base64) return false;
    try {
      const blob = base64ToBlob(base64, 'application/pdf');
      const path = `${agencyId}/paal.pdf`;
      const { error: upErr } = await supabase.storage
        .from('paal-files').upload(path, blob, { upsert: true });
      if (upErr) { console.error('uploadPaalFile storage error:', upErr); return false; }
      // Track filename + timestamp on the agency row
      const { error: dbErr } = await supabase.from('agencies').update({
        paal_file_name: fileName || 'paal.pdf',
        paal_uploaded_at: new Date().toISOString(),
      }).eq('id', agencyId);
      if (dbErr) { console.error('uploadPaalFile db error:', dbErr); return false; }
      return true;
    } catch (e) {
      console.error('uploadPaalFile threw:', e);
      return false;
    }
  },

  // Retrieve the agency's PAAL PDF as base64 (or null if none on file).
  // Useful for re-extraction or download.
  async getPaalFile(agencyId = null) {
    const id = agencyId || await currentAgencyId();
    if (!id) return null;
    const { data, error } = await supabase.storage
      .from('paal-files').download(`${id}/paal.pdf`);
    if (error || !data) return null;
    const full = await blobToBase64(data);
    return full.split(',')[1]; // strip "data:application/pdf;base64," prefix
  },

  // Get metadata about the on-file PAAL (file name, upload date) without
  // downloading the file itself. Returns { fileName, uploadedAt } or null.
  async getPaalMeta(agencyId = null) {
    const id = agencyId || await currentAgencyId();
    if (!id) return null;
    const { data, error } = await supabase.from('agencies')
      .select('paal_file_name, paal_uploaded_at').eq('id', id).maybeSingle();
    if (error || !data?.paal_file_name) return null;
    return { fileName: data.paal_file_name, uploadedAt: data.paal_uploaded_at };
  },

  // Save the structured PAAL thresholds (from Penny's extraction or admin's
  // hand-edits) to the doc_template.paal JSON. This is what the docx builder
  // reads at draft time for the Term-section approver language.
  async savePaalThresholds(agencyId, paalObj) {
    if (!agencyId) return false;
    // Merge into existing doc_template rather than overwrite — preserves
    // drafting_voice, font, indents, etc.
    const { data: existing } = await supabase.from('agencies')
      .select('doc_template').eq('id', agencyId).maybeSingle();
    const merged = { ...(existing?.doc_template || {}), paal: paalObj };
    const { error } = await supabase.from('agencies')
      .update({ doc_template: merged }).eq('id', agencyId);
    if (error) { console.error('savePaalThresholds error:', error); return false; }
    return true;
  },

  // Save the Term-clause configuration (sub-letters and review metadata) to
  // doc_template.term_clause. This is what the docx builder reads to build
  // Section 2 (TERM OF PERFORMANCE) of every Exhibit A.
  //
  // termClauseObj shape:
  //   {
  //     subletters: [{ id, text, source, active }, ...],
  //     last_reviewed_at: ISO timestamp,
  //     last_reviewed_against: [template_id, ...]
  //   }
  async saveTermClause(agencyId, termClauseObj) {
    if (!agencyId) return false;
    const { data: existing } = await supabase.from('agencies')
      .select('doc_template').eq('id', agencyId).maybeSingle();
    const merged = { ...(existing?.doc_template || {}), term_clause: termClauseObj };
    const { error } = await supabase.from('agencies')
      .update({ doc_template: merged }).eq('id', agencyId);
    if (error) { console.error('saveTermClause error:', error); return false; }
    return true;
  },

  // Save the Exhibit B template configuration to doc_template.exhibit_b_template.
  // The docx builder reads this as the baseline for every Exhibit B draft —
  // Penny then customizes per-contract (budget figures, payment schedule based
  // on deliverables, etc.) starting from this baseline rather than synthesizing
  // from scratch.
  //
  // exhibitBTemplateObj shape:
  //   {
  //     sections: [{ id, heading, text, source, active }, ...],
  //     last_reviewed_at: ISO timestamp,
  //     last_reviewed_against: [template_id, ...]
  //   }
  async saveExhibitBTemplate(agencyId, exhibitBTemplateObj) {
    if (!agencyId) return false;
    const { data: existing } = await supabase.from('agencies')
      .select('doc_template').eq('id', agencyId).maybeSingle();
    const merged = { ...(existing?.doc_template || {}), exhibit_b_template: exhibitBTemplateObj };
    const { error } = await supabase.from('agencies')
      .update({ doc_template: merged }).eq('id', agencyId);
    if (error) { console.error('saveExhibitBTemplate error:', error); return false; }
    return true;
  },

  // Save the Exhibit D template configuration to doc_template.exhibit_d_template.
  // Mirrors the Exhibit B pattern — agency-level baseline that the docx builder
  // reads for Special Terms and Conditions. Per-contract customization happens
  // at draft time.
  //
  // exhibitDTemplateObj shape: same as exhibitBTemplateObj.
  async saveExhibitDTemplate(agencyId, exhibitDTemplateObj) {
    if (!agencyId) return false;
    const { data: existing } = await supabase.from('agencies')
      .select('doc_template').eq('id', agencyId).maybeSingle();
    const merged = { ...(existing?.doc_template || {}), exhibit_d_template: exhibitDTemplateObj };
    const { error } = await supabase.from('agencies')
      .update({ doc_template: merged }).eq('id', agencyId);
    if (error) { console.error('saveExhibitDTemplate error:', error); return false; }
    return true;
  },

  // Save the Clause Library to doc_template.clause_library.
  // clause_library shape: { A: [...clauses], B: [...clauses], D: [...clauses], Sol: [...clauses] }
  // Each clause: { id, sectionName, language, locked, required, sortOrder, createdAt, updatedAt }
  async saveClauseLibrary(agencyId, clauseLibrary) {
    if (!agencyId) return false;
    const { data: existing } = await supabase.from('agencies')
      .select('doc_template').eq('id', agencyId).maybeSingle();
    const merged = { ...(existing?.doc_template || {}), clause_library: clauseLibrary };
    const { error } = await supabase.from('agencies')
      .update({ doc_template: merged }).eq('id', agencyId);
    if (error) { console.error('saveClauseLibrary error:', error); return false; }
    return true;
  },
};

// Helper — current user's agency id
async function currentAgencyId() {
  const userId = await getUserId();
  if (!userId) return null;
  const { data } = await supabase.from('user_profiles')
    .select('agency_id').eq('id', userId).single();
  return data?.agency_id || null;
}

// ── Agency Library (shared, stored in Supabase Storage) ─────────────
export const libraryDb = {
  async get(key) {
    const userId = await getUserId();
    if (!userId) return null;
    const { data: profile } = await supabase
      .from('user_profiles').select('agency_id').eq('id', userId).single();
    if (!profile?.agency_id) return null;
    const { data, error } = await supabase.storage
      .from('library-files').download(`${profile.agency_id}/${key}`);
    if (error) return null;
    const full = await blobToBase64(data);
    return { base64: full.split(',')[1] };
  },

  async listKeys() {
    const userId = await getUserId();
    if (!userId) return [];
    const { data: profile } = await supabase
      .from('user_profiles').select('agency_id').eq('id', userId).single();
    if (!profile?.agency_id) return [];
    const { data } = await supabase.from('agency_library').select('key, name, file_size')
      .eq('agency_id', profile.agency_id);
    return data || [];
  },
};

// ── Jurisdiction hierarchy ──────────────────────────────────────────
// The jurisdiction_nodes table is a tree: root → state → jurisdiction →
// locality. Each node carries a materialized `path` uuid[] (root-first,
// including self), maintained by a DB trigger. Agencies point at one node
// via agencies.jurisdiction_node_id; shared_library documents point at one
// node via shared_library.scope_node_id. A document is visible to an agency
// when the document's node is in the agency's node's path — enforced by RLS.
//
// Level constraints (enforced in the UI, not the DB): a node's level depends
// on its parent — root→state, state→jurisdiction|locality,
// jurisdiction→locality, locality→locality. See LEVEL_CHILDREN below.
export const LEVEL_CHILDREN = {
  root:         ['state'],
  state:        ['jurisdiction', 'locality'],
  jurisdiction: ['locality'],
  locality:     ['locality'],
};

export const jurisdictionDb = {
  // Return the entire tree as a flat array. Each row: { id, parent_id,
  // level, name, path, created_at }. The UI builds the visual hierarchy
  // from parent_id / path. Ordered by path so parents always precede
  // their children.
  async listNodes() {
    const { data, error } = await supabase.from('jurisdiction_nodes')
      .select('id, parent_id, level, name, path, created_at');
    if (error) { console.error('jurisdictionDb.listNodes error:', error); return []; }
    // Sort by path length then name — parents before children, stable order
    return (data || []).sort((a, b) => {
      const la = (a.path || []).length, lb = (b.path || []).length;
      if (la !== lb) return la - lb;
      return (a.name || '').localeCompare(b.name || '');
    });
  },

  // Create a node. The DB trigger computes `path` automatically. Caller is
  // responsible for passing a valid level for the parent (UI enforces this
  // via LEVEL_CHILDREN). parent_id null only allowed for the root, which
  // already exists from the Stage 1 migration — so in practice parent_id
  // should always be set here.
  async createNode({ parent_id, level, name }) {
    if (!parent_id || !level || !name?.trim()) {
      return { ok: false, reason: 'missing_fields' };
    }
    const { data, error } = await supabase.from('jurisdiction_nodes')
      .insert({ parent_id, level, name: name.trim() })
      .select('id, parent_id, level, name, path, created_at')
      .single();
    if (error) {
      console.error('jurisdictionDb.createNode error:', error);
      // Unique-sibling violation → friendly reason
      if (error.code === '23505') return { ok: false, reason: 'duplicate_name' };
      return { ok: false, reason: error.message };
    }
    return { ok: true, node: data };
  },

  // Rename a node. Does not affect path (path is id-based, not name-based).
  async renameNode(id, name) {
    if (!id || !name?.trim()) return { ok: false, reason: 'missing_fields' };
    const { error } = await supabase.from('jurisdiction_nodes')
      .update({ name: name.trim() }).eq('id', id);
    if (error) {
      console.error('jurisdictionDb.renameNode error:', error);
      if (error.code === '23505') return { ok: false, reason: 'duplicate_name' };
      return { ok: false, reason: error.message };
    }
    return { ok: true };
  },

  // Delete a node. BLOCKED if anything still depends on it: child nodes,
  // agencies pointing at it, or shared_library docs scoped to it. Per the
  // agreed design (Decision 2a), we just report what's blocking — the admin
  // reassigns dependents first, then retries.
  async deleteNode(id) {
    if (!id) return { ok: false, reason: 'missing_fields' };

    // Count dependents
    const [{ count: childCount }, { count: agencyCount }, { count: docCount }] = await Promise.all([
      supabase.from('jurisdiction_nodes').select('id', { count: 'exact', head: true }).eq('parent_id', id),
      supabase.from('agencies').select('id', { count: 'exact', head: true }).eq('jurisdiction_node_id', id),
      supabase.from('shared_library').select('key', { count: 'exact', head: true }).eq('scope_node_id', id),
    ]);

    if (childCount > 0 || agencyCount > 0 || docCount > 0) {
      return {
        ok: false,
        reason: 'has_dependents',
        childCount: childCount || 0,
        agencyCount: agencyCount || 0,
        docCount: docCount || 0,
      };
    }

    // Also refuse to delete the root
    const { data: node } = await supabase.from('jurisdiction_nodes')
      .select('level').eq('id', id).maybeSingle();
    if (node?.level === 'root') return { ok: false, reason: 'cannot_delete_root' };

    const { error } = await supabase.from('jurisdiction_nodes').delete().eq('id', id);
    if (error) { console.error('jurisdictionDb.deleteNode error:', error); return { ok: false, reason: error.message }; }
    return { ok: true };
  },

  // Assign an agency to a jurisdiction node.
  async setAgencyNode(agencyId, nodeId) {
    if (!agencyId || !nodeId) return false;
    const { error } = await supabase.from('agencies')
      .update({ jurisdiction_node_id: nodeId }).eq('id', agencyId);
    if (error) { console.error('jurisdictionDb.setAgencyNode error:', error); return false; }
    return true;
  },
};

// ── Shared Library (super admin managed) ────────────────────────────
export const sharedLibraryDb = {
  // List shared library documents. RLS scopes this automatically: a normal
  // user sees only docs whose scope_node_id is in their agency's node path;
  // the super admin sees everything. We now also select scope_node_id so the
  // Super Admin UI can show/edit each doc's jurisdiction scope.
  async list() {
    const { data, error } = await supabase.from('shared_library')
      .select('key, name, file_size, scope_node_id, uploaded_at, uploaded_by')
      .order('uploaded_at', { ascending: true });
    if (error) { console.error('sharedLibraryDb.list error:', error); return []; }
    return data || [];
  },

  async get(key) {
    const { data, error } = await supabase.storage
      .from('library-files').download(`shared/${key}`);
    if (error) return null;
    const full = await blobToBase64(data);
    const { data: meta } = await supabase.from('shared_library').select('*').eq('key', key).single();
    return { base64: full.split(',')[1], name: meta?.name || key, size: meta?.file_size };
  },

  // Upload (or replace) a shared library document. scopeNodeId assigns the
  // jurisdiction scope — pass the node id the document should attach to. If
  // omitted, the document is left with whatever scope it already had (on
  // replace) or null (on fresh insert). The Super Admin UI always passes it.
  async set(key, fileData, scopeNodeId = null) {
    const blob = base64ToBlob(fileData.base64, 'application/pdf');
    const { error: se } = await supabase.storage
      .from('library-files').upload(`shared/${key}`, blob, { upsert: true });
    if (se) throw new Error('Storage: ' + se.message);
    const userId = await getUserId();
    // upsert must resolve conflicts on the `key` column — the table's unique
    // constraint is shared_library_key_key (on `key`), NOT the primary key.
    // Without onConflict, Supabase defaults to PK conflict detection, treats a
    // re-upload of an existing key as an INSERT, and trips the unique
    // constraint. onConflict: 'key' makes a re-upload correctly UPDATE the row.
    const row = {
      key, name: fileData.name, file_size: fileData.size,
      uploaded_by: userId, uploaded_at: new Date().toISOString(),
    };
    // Only include scope_node_id when explicitly provided, so a re-upload
    // that doesn't specify it doesn't clobber an existing scope with null.
    if (scopeNodeId) row.scope_node_id = scopeNodeId;
    const { error: de } = await supabase.from('shared_library').upsert(row, { onConflict: 'key' });
    if (de) throw new Error('Database: ' + de.message);
    return true;
  },

  // Re-scope an existing document without re-uploading the file.
  async setScope(key, scopeNodeId) {
    if (!key || !scopeNodeId) return false;
    const { error } = await supabase.from('shared_library')
      .update({ scope_node_id: scopeNodeId }).eq('key', key);
    if (error) { console.error('sharedLibraryDb.setScope error:', error); return false; }
    return true;
  },

  async del(key) {
    await supabase.storage.from('library-files').remove([`shared/${key}`]);
    await supabase.from('shared_library').delete().eq('key', key);
    return true;
  },
};

// ── Local settings (UI prefs only) ──────────────────────────────────
export const localSettings = {
  get: (key) => { try { return JSON.parse(localStorage.getItem('gs_' + key)); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem('gs_' + key, JSON.stringify(value)); } catch {} },
};

// ── Legacy shim ─────────────────────────────────────────────────────
const PREFIX = 'gs_legacy_';
export const store = {
  async get(key) { try { return JSON.parse(localStorage.getItem(PREFIX + key)); } catch { return null; } },
  async set(key, value) { try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); return true; } catch { return false; } },
  async del(key) { try { localStorage.removeItem(PREFIX + key); return true; } catch { return false; } },
  getUsage() {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) total += (localStorage.getItem(k) || '').length * 2;
    }
    return { bytes: total, kb: Math.round(total / 1024), mb: (total / 1024 / 1024).toFixed(2) };
  },
};

export default store;
